import { fetchJSON as fetchAPI } from './client';

export type Dependency = { key: string; id: string; name: string; type: string; status: string; description?: string };
export type Objective = { key: string; name: string; category: string; description?: string; mandatory: boolean };
export type ExecutiveHeaderField = { key: string; name: string; description?: string; status: string };
export type HealthCardSpec = { key: string; name: string; metric?: string; drill_down?: string; supports?: string[]; status: string };
export type OpportunityField = { key: string; name: string; required: boolean };
export type PriorityLevel = { key: string; name: string; order: number; status: string };
export type ProviderInterface = { key: string; name: string; interface: string; description?: string; status: string };
export type FrontendFeature = { key: string; name: string; category: string; description?: string; status: string };
export type DatabaseTable = { key: string; name: string; service: string; description?: string; has_rls: boolean };
export type BackendAPI = { key: string; method: string; path: string; description?: string; auth_required: boolean };
export type ObservabilityEvent = { key: string; event: string; category: string };
export type SecurityControl = { key: string; name: string; category: string; description?: string; status: string };
export type TestingRequirement = { key: string; name: string; test_type: string; location?: string };
export type QualityGate = { key: string; name: string; command?: string; description?: string; blocking: boolean };
export type OutOfScopeItem = { key: string; name: string; reason?: string };
export type SuccessCriterion = { key: string; criterion: string; validation?: string; mandatory: boolean };

export type SprintDashboard = {
  sprint_id: string; tenant_id: string; version: string; priority: string; applies_to: string[];
  dependencies: number; objectives: number; executive_header_fields: number; health_cards: number;
  opportunity_fields: number; priority_levels: number; provider_interfaces: number;
  frontend_features: number; database_tables: number; backend_apis: number; observability_events: number;
  security_controls: number; testing_requirements: number; quality_gates: number;
  out_of_scope: number; success_criteria: number; generated_at: string;
};

export type SprintValidation = {
  valid: boolean; dashboard_loads_under_2s: boolean; accessibility_passes: boolean;
  responsive_layouts_verified: boolean; permission_checks_enforced: boolean;
  service_interfaces_isolated: boolean; tests_pass: boolean; ci_passes: boolean;
  observability_active: boolean; gaps?: string[]; evaluated_at: string;
};

export type Dev103Status = {
  sprint_id: string; version: string; priority: string; provider_interfaces: number;
  backend_apis: number; health_cards: number; quality_gates: number;
  default_landing: boolean; command_center: boolean; production_ready: boolean;
  provider_driven: boolean; status: string;
};

const GOVERNANCE_BASE = import.meta.env.VITE_GOVERNANCE_API ?? '/api/v1/governance';
const fetchG = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${GOVERNANCE_BASE}${path}`, init);

export const dailyGrowthCommandCenterDev103Api = {
  dashboard: () => fetchG<SprintDashboard>('/dev103/dashboard'),
  dependencies: () => fetchG<{ dependencies: Dependency[] }>('/dev103/dependencies').then((r) => r.dependencies),
  objectives: () => fetchG<{ objectives: Objective[] }>('/dev103/objectives').then((r) => r.objectives),
  executiveHeader: () => fetchG<{ fields: ExecutiveHeaderField[] }>('/dev103/executive-header').then((r) => r.fields),
  businessHealthCards: () => fetchG<{ cards: HealthCardSpec[] }>('/dev103/business-health-cards').then((r) => r.cards),
  opportunityInbox: () => fetchG<{ fields: OpportunityField[] }>('/dev103/opportunity-inbox').then((r) => r.fields),
  todaysPriorities: () => fetchG<{ levels: PriorityLevel[] }>('/dev103/todays-priorities').then((r) => r.levels),
  serviceArchitecture: () => fetchG<{ providers: ProviderInterface[] }>('/dev103/service-architecture').then((r) => r.providers),
  frontendFeatures: () => fetchG<{ features: FrontendFeature[] }>('/dev103/frontend-features').then((r) => r.features),
  databaseSchema: () => fetchG<{ tables: DatabaseTable[] }>('/dev103/database-schema').then((r) => r.tables),
  backendAPIs: () => fetchG<{ apis: BackendAPI[] }>('/dev103/backend-apis').then((r) => r.apis),
  observability: () => fetchG<{ events: ObservabilityEvent[] }>('/dev103/observability').then((r) => r.events),
  security: () => fetchG<{ controls: SecurityControl[] }>('/dev103/security').then((r) => r.controls),
  testing: () => fetchG<{ requirements: TestingRequirement[] }>('/dev103/testing').then((r) => r.requirements),
  qualityGates: () => fetchG<{ gates: QualityGate[] }>('/dev103/quality-gates').then((r) => r.gates),
  outOfScope: () => fetchG<{ items: OutOfScopeItem[] }>('/dev103/out-of-scope').then((r) => r.items),
  successCriteria: () => fetchG<{ criteria: SuccessCriterion[] }>('/dev103/success-criteria').then((r) => r.criteria),
  validate: (attrs: Record<string, boolean>) =>
    fetchG<SprintValidation>('/dev103/validate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attrs }) }),
  status: () => fetchG<Dev103Status>('/dev103/status'),
};
